/**
 * DiscountComponent handles manual discount code application and rendering in the cart drawer section.
 *
 * @class DiscountComponent
 * @extends HTMLElement
 */
class DiscountComponent extends HTMLElement {
    /**
     * Initializes the DiscountComponent by setting up form references and event listeners.
     */
    constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.discountForm = this.querySelector(`#discount-${this.sectionId}`);
        this.sectionToQuery = ['cart-drawer'];
        document.addEventListener(
            _EVENT_HELPER.updateDiscountLayout,
            this.renderDiscountCodeLayout.bind(this)
        );
        this.addEventListener(
            _EVENT_HELPER.removeDiscountCode,
            this.removeDiscountCode.bind(this)
        );
        this.discountCodeInput = this.querySelector('[name="discount-code"]');
        this.giftCode = this.dataset.giftCode || '';
        this.giftCodeNormalized = this._normalizeDiscountCode(this.giftCode);
        this.giftVariantId = Number(this.dataset.giftVariantId || 0);
        this.giftProductId = Number(this.dataset.giftProductId || 0);
        this.giftMinSubtotalCents = Number(
            this.dataset.giftMinSubtotalCents || 0
        );
        this._giftPromotionInFlight = false;
        this._giftPromotionReconcileTimer = null;
        this._boundScheduleGiftPromotionReconcile =
            this.scheduleGiftPromotionReconcile.bind(this);
        document.addEventListener(
            _EVENT_HELPER.triggerCartChange,
            this._boundScheduleGiftPromotionReconcile
        );
        document.addEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this._boundScheduleGiftPromotionReconcile
        );
        if (this.discountForm) {
            this.init();
            this.syncAcceptedDiscountCodesFromCart();
        } else {
            console.warn(
                'Discount form not found. Ensure the form exists in your HTML'
            );
        }
    }

    /**
     * Attaches the submit event listener to the discount form element.
     * @private
     */
    init() {
        this.discountForm.addEventListener('submit', (e) => {
            e.preventDefault();
            this.submitDiscountForm(e);
        });
    }

    /**
     * Normalizes discount code strings for comparisons without changing the
     * customer-entered value sent to Shopify.
     *
     * @param {string} code
     * @returns {string}
     * @private
     */
    _normalizeDiscountCode(code) {
        return String(code || '').trim().toUpperCase();
    }

    /**
     * Checks whether the coupon-triggered gift flow is configured.
     *
     * @returns {boolean}
     * @private
     */
    _isGiftPromotionConfigured() {
        return (
            this.dataset.giftPromotionEnabled === 'true' &&
            !!this.giftCodeNormalized &&
            Number.isFinite(this.giftVariantId) &&
            this.giftVariantId > 0
        );
    }

    /**
     * Checks whether a submitted or removed code matches the configured gift code.
     *
     * @param {string} code
     * @returns {boolean}
     * @private
     */
    _isGiftCode(code) {
        return (
            this._isGiftPromotionConfigured() &&
            this._normalizeDiscountCode(code) === this.giftCodeNormalized
        );
    }

    /**
     * Returns the localized message configured on the component.
     *
     * @param {'belowMinimum'|'unavailable'|'alreadyUsed'|'combinationConflict'|'networkError'|'genericError'} type
     * @returns {string}
     * @private
     */
    _getGiftMessage(type) {
        const messageMap = {
            belowMinimum: this.dataset.giftMsgBelowMinimum,
            unavailable: this.dataset.giftMsgUnavailable,
            alreadyUsed: this.dataset.giftMsgAlreadyUsed,
            combinationConflict: this.dataset.giftMsgCombinationConflict,
            networkError: this.dataset.giftMsgNetworkError,
            genericError: this.dataset.giftMsgGenericError,
        };

        return messageMap[type] || messageMap.genericError || '';
    }

    /**
     * Shows the accessible gift-promotion message slot.
     *
     * @param {'belowMinimum'|'unavailable'|'alreadyUsed'|'combinationConflict'|'networkError'|'genericError'} type
     * @private
     */
    showGiftPromotionMessage(type) {
        const messageEl = this.querySelector('[data-gift-message]');
        const textEl = this.querySelector('[data-gift-message-text]');
        const message = this._getGiftMessage(type);

        this.hideMessageTimmer && clearTimeout(this.hideMessageTimmer);
        [
            ...this.querySelectorAll(
                '[discount-error], [discount-success], [discount-shipping-error], [data-gift-message]'
            ),
        ]?.forEach((messageItem) => {
            messageItem.setAttribute('hidden', 'true');
        });
        if (!messageEl || !textEl || !message) return;

        textEl.textContent = message;
        messageEl.removeAttribute('hidden');
    }

    /**
     * Hides the accessible gift-promotion message slot.
     *
     * @private
     */
    hideGiftPromotionMessage() {
        const messageEl = this.querySelector('[data-gift-message]');
        if (messageEl) {
            messageEl.setAttribute('hidden', 'true');
        }
    }

    /**
     * Low-level JSON request helper used by the gift-coupon flow.
     *
     * @param {string} url
     * @param {Object} options
     * @returns {Promise<Object>}
     * @private
     */
    async _fetchCartJson(url, options = {}) {
        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
                ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                ...(options.headers || {}),
            },
            ...options,
        });

        let payload = null;
        try {
            payload = await response.json();
        } catch (error) {
            if (response.ok) {
                throw new Error('Invalid JSON response');
            }
        }

        if (!response.ok) {
            const error = new Error(payload?.message || response.statusText);
            error.status = response.status;
            error.payload = payload;
            throw error;
        }

        if (!payload) {
            throw new Error('Invalid JSON response');
        }

        return payload;
    }

    /**
     * Retrieves all currently applied discount codes from the DOM.
     *
     * @return {string[]} Array of applied discount code strings.
     * @private
     */
    _getExisitingDiscountsCode() {
        return [
            ...this.querySelectorAll('[manual-discount-code] [data-code]'),
        ].map((code) => code.dataset.code);
    }

    /**
     * Extracts discount codes (pills) from section HTML.
     * @param {string} html
     * @returns {string[]}
     * @private
     */
    _extractDiscountPillsFromHtml(html) {
        try {
            if (!html) return [];
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const pills = Array.from(
                doc.querySelectorAll('[manual-discount-code] [data-code]')
            );
            return pills
                .map((li) =>
                    li instanceof HTMLLIElement &&
                    typeof li.dataset.code === 'string'
                        ? li.dataset.code
                        : null
                )
                .filter(Boolean);
        } catch {
            return [];
        }
    }

    /**
     * Escapes customer-facing discount codes before injecting fallback pills.
     *
     * @param {string} value
     * @returns {string}
     * @private
     */
    _escapeHtml(value) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    /**
     * Extracts accepted discount codes from a Shopify Ajax response.
     *
     * @param {Object} response
     * @returns {string[]}
     * @private
     */
    _getAcceptedDiscountCodesFromResponse(response) {
        return [
            ...new Set(
                (response?.discount_codes || [])
                    .filter((discount) => discount?.applicable !== false)
                    .map((discount) => discount.code)
                    .filter(Boolean)
            ),
        ];
    }

    /**
     * Adds accepted discount-code pills that Liquid did not render.
     *
     * @param {string[]} fallbackCodes
     * @private
     */
    _appendFallbackDiscountCodePills(fallbackCodes = []) {
        const discountCodeList = this.querySelector('[manual-discount-code]');
        if (!discountCodeList) return;

        const renderedCodes = [
            ...discountCodeList.querySelectorAll('[data-code]'),
        ].map((item) => this._normalizeDiscountCode(item.dataset.code));

        fallbackCodes.forEach((code) => {
            if (!code || renderedCodes.includes(this._normalizeDiscountCode(code))) {
                return;
            }

            const escapedCode = this._escapeHtml(code);
            discountCodeList.insertAdjacentHTML(
                'beforeend',
                `<li class="d_badge fnt-14 d-flex align-items-center column-gap-4" data-code="${escapedCode}">
                    ${escapedCode}
                    <remove-discount-pill class="d-block cursor-pointer">
                        <button type="button" class="p-0 border-0 bg-transparent" aria-label="Remover ${escapedCode}">&times;</button>
                    </remove-discount-pill>
                </li>`
            );
            renderedCodes.push(this._normalizeDiscountCode(code));
        });
    }

    /**
     * Syncs accepted discount-code pills from cart JSON on initial render.
     */
    syncAcceptedDiscountCodesFromCart() {
        this._getCart()
            .then((cart) => {
                this._appendFallbackDiscountCodePills(
                    this._getDiscountCodesFromCart(cart)
                );
            })
            .catch((error) => {
                console.error('Error syncing accepted discount codes:', error);
            });
    }

    /**
     * Clears the discount code input field.
     * @private
     */
    emptyDiscountInput() {
        this.discountCodeInput.value = '';
    }

    /**
     * Renders discount badge layout based on updated sections HTML.
     *
     * @param {Object} sections - The HTML sections payload containing manual-discount-codes.
     * @throws {Error} When manual-discount-codes section is missing.
     */
    renderDiscountCodeLayout(sections, fallbackCodes = []) {
        if (!sections.hasOwnProperty('manual-discount-codes'))
            throw new Error(
                'Missing "manual-discount-codes.liquid" file or manual-discount-codes section was not requested during section rendering'
            );
        const getDiscountBadgeCode = HELPER_UTIL.getHTMLFromString(
            sections['manual-discount-codes'],
            '[manual-discount-code]'
        );
        const discountCodeList = this.querySelector('[manual-discount-code]');
        discountCodeList.innerHTML = getDiscountBadgeCode.innerHTML;
        this._appendFallbackDiscountCodePills(fallbackCodes);
    }

    /**
     * Hides all discount message elements (error and success) after an optional delay.
     *
     * @param {number} [hideTimer=0] - Delay in milliseconds before hiding messages.
     * @private
     */
    hideAllDiscountMessages(hideTimer = 0) {
        this.hideMessageTimmer && clearTimeout(this.hideMessageTimmer);

        this.hideMessageTimmer = setTimeout(() => {
            [
                ...this.querySelectorAll(
                    '[discount-error], [discount-success], [discount-shipping-error], [data-gift-message]'
                ),
            ]?.forEach((message) => {
                message.setAttribute('hidden', 'true');
            });
        }, hideTimer);
    }

    /**
     * Shows the discount message element corresponding to the given type.
     *
     * @param {'error'|'success'} messageType - The type of message to display.
     * @private
     */
    showDiscountMessages(messageType) {
        // Hide all message
        this.hideAllDiscountMessages();
        // Show message based on "Message type"
        switch (messageType) {
            case 'error':
                this.querySelector(
                    `[discount-${messageType}]`
                )?.removeAttribute('hidden');
                break;
            case 'success':
                this.querySelector(
                    `[discount-${messageType}]`
                )?.removeAttribute('hidden');
                break;
            case 'shipping-error':
                this.querySelector(
                    `[discount-${messageType}]`
                )?.removeAttribute('hidden');
        }
    }

    /**
     * Handles removal of a discount code when remove event is dispatched.
     *
     * @param {CustomEvent} e - Event containing discount code details.
     * @private
     */
    removeDiscountCode(e) {
        if (e.detail.preventBubble) {
            e.preventDefault();
            e.stopPropagation();
        }
        const discountCode = e.detail.code;
        const existingCodes = this._getExisitingDiscountsCode();

        // remove discount code from existing codes
        const newCodes = existingCodes.filter((code) => code !== discountCode);
        if (this._isGiftCode(discountCode)) {
            this.removeGiftPromotionDiscount(newCodes);
            return;
        }

        if (newCodes.length) {
            this.applyDiscount(newCodes.join(','));
        } else {
            this.applyDiscount('');
        }
    }

    /**
     * Processes the discount form submission, prepares codes, and applies them.
     *
     * @param {SubmitEvent} e - The form submission event.
     * @private
     */
    submitDiscountForm(e) {
        this.discountFormData = new FormData(this.discountForm);
        const enteredCode = this.discountFormData.get('discount-code').trim();

        if (enteredCode.length === 0) return;

        const existingCodes = this._getExisitingDiscountsCode();
        const modifyToUpperCase = existingCodes.map(el => el.toUpperCase());

        if (this._isGiftCode(enteredCode)) {
            this.submitGiftPromotionDiscount(enteredCode, existingCodes);
            return;
        }

        // If user is adding same code again, prevent making ajax call
        if (modifyToUpperCase.includes(enteredCode.toUpperCase())) return;

        const discountCode = existingCodes.length
            ? `${enteredCode},${existingCodes.join(',')}`
            : enteredCode;
        // Apply discount code by making ajax call
        this.applyDiscount(discountCode, 'add');
    }

    /**
     * Sends AJAX request to apply or remove discount codes on the cart.
     *
     * @param {string} discountCode - Comma-separated discount codes to apply.
     * @param {string} [discountAction] - Action type ('add') to trigger success messaging.
     * @private
     */
    applyDiscount(discountCode, discountAction) {
        const submitButton = this.querySelector('button[type="submit"]');
        submitButton?.classList.add('disable');

        this._applyDiscountRequest(discountCode)
            .then((response) => {
                this.checkDiscountCodeValidationResponse(
                    response,
                    discountAction
                );
            })
            .catch((error) => {
                console.error('Error applying discount:', error);
            })
            .finally(() => {
                submitButton?.classList.remove('disable');
            });
    }

    /**
     * Sends AJAX request to update cart discount codes and return the raw response.
     *
     * @param {string} discountCode - Comma-separated discount codes to apply.
     * @returns {Promise<Object>}
     * @private
     */
    _applyDiscountRequest(discountCode) {
        const payload = {
            discount: discountCode || '',
            sections: 'manual-discount-codes',
        };

        return this._fetchCartJson(window.routes.cart_update_url, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    /**
     * Retrieves the current cart JSON.
     *
     * @returns {Promise<Object>}
     * @private
     */
    _getCart() {
        return this._fetchCartJson(`${window.Shopify.routes.root}cart.js`);
    }

    /**
     * Adds the configured gift variant as an internal promotional line.
     *
     * @returns {Promise<Object>}
     * @private
     */
    _addGiftPromotionLine() {
        return this._fetchCartJson(window.routes.cart_add_url, {
            method: 'POST',
            body: JSON.stringify({
                items: [
                    {
                        id: this.giftVariantId,
                        quantity: 1,
                        properties: {
                            _promo_gift: this.giftCodeNormalized,
                        },
                    },
                ],
            }),
        });
    }

    /**
     * Changes a cart line quantity by its real line item key.
     *
     * @param {string} lineItemKey
     * @param {number} quantity
     * @returns {Promise<Object>}
     * @private
     */
    _changeCartLineQuantity(lineItemKey, quantity) {
        return this._fetchCartJson(window.routes.cart_change_url, {
            method: 'POST',
            body: JSON.stringify({
                id: lineItemKey,
                quantity,
            }),
        });
    }

    /**
     * Checks whether a cart item is the internal coupon gift line.
     *
     * @param {Object} item
     * @returns {boolean}
     * @private
     */
    _isGiftPromotionLine(item) {
        const promoCode = item?.properties?._promo_gift;
        return this._normalizeDiscountCode(promoCode) === this.giftCodeNormalized;
    }

    /**
     * Finds the internal coupon gift line in cart JSON.
     *
     * @param {Object} cart
     * @returns {Object|null}
     * @private
     */
    _findGiftPromotionLine(cart) {
        return (cart?.items || []).find((item) =>
            this._isGiftPromotionLine(item)
        ) || null;
    }

    /**
     * Calculates the preliminary eligible subtotal in cents.
     * Shopify remains the financial source of truth; this is only preventive UX.
     *
     * @param {Object} cart
     * @returns {number}
     * @private
     */
    _getGiftEligibleSubtotal(cart) {
        return (cart?.items || []).reduce((subtotal, item) => {
            if (this._isGiftPromotionLine(item)) return subtotal;
            return subtotal + Number(item.final_line_price || 0);
        }, 0);
    }

    /**
     * Extracts applied discount code titles from cart JSON.
     *
     * @param {Object} cart
     * @returns {string[]}
     * @private
     */
    _getDiscountCodesFromCart(cart) {
        const discountCodes = [];
        (cart?.discount_codes || []).forEach((discount) => {
            if (discount.applicable !== false && discount.code) {
                discountCodes.push(discount.code);
            }
        });
        (cart?.cart_level_discount_applications || []).forEach((discount) => {
            if (discount.type === 'discount_code' && discount.title) {
                discountCodes.push(discount.title);
            }
        });
        (cart?.items || []).forEach((item) => {
            (item.line_level_discount_allocations || []).forEach(
                (allocation) => {
                    const application = allocation.discount_application;
                    if (
                        application?.type === 'discount_code' &&
                        application.title
                    ) {
                        discountCodes.push(application.title);
                    }
                }
            );
        });

        return [...new Set(discountCodes)];
    }

    /**
     * Removes the configured gift code from a code array, case-insensitively.
     *
     * @param {string[]} codes
     * @returns {string[]}
     * @private
     */
    _withoutGiftCode(codes) {
        return (codes || []).filter(
            (code) => this._normalizeDiscountCode(code) !== this.giftCodeNormalized
        );
    }

    /**
     * Returns a comma-separated discount payload preserving existing non-gift codes.
     *
     * @param {string[]} existingCodes
     * @param {string} enteredCode
     * @returns {string}
     * @private
     */
    _getDiscountPayloadWithGift(existingCodes, enteredCode) {
        const codes = [enteredCode, ...this._withoutGiftCode(existingCodes)];
        const uniqueCodes = [];

        codes.forEach((code) => {
            if (!code) return;
            const normalizedCode = this._normalizeDiscountCode(code);
            const alreadyAdded = uniqueCodes.some(
                (existingCode) =>
                    this._normalizeDiscountCode(existingCode) === normalizedCode
            );
            if (!alreadyAdded) uniqueCodes.push(code);
        });

        return uniqueCodes.join(',');
    }

    /**
     * Checks whether Shopify accepted the configured gift code.
     *
     * @param {Object} response
     * @returns {boolean}
     * @private
     */
    _isGiftCodeApplicable(response) {
        const responseDiscountCodeAccepted = (response?.discount_codes || []).some(
            (discount) => {
                return (
                    this._normalizeDiscountCode(discount.code) ===
                        this.giftCodeNormalized && discount.applicable === true
                );
            }
        );
        if (responseDiscountCodeAccepted) return true;

        const cartDiscountCodeAccepted = this._getDiscountCodesFromCart(
            response
        ).some((code) => this._isGiftCode(code));
        if (cartDiscountCodeAccepted) return true;

        const renderedCodes = this._extractDiscountPillsFromHtml(
            response?.sections?.['manual-discount-codes']
        );
        return renderedCodes.some((code) => this._isGiftCode(code));
    }

    /**
     * Applies an updated discount code list, renders pills, and syncs cart UI.
     *
     * @param {string[]} codes
     * @returns {Promise<Object>}
     * @private
     */
    async _applyDiscountCodesAndRefresh(codes) {
        const response = await this._applyDiscountRequest((codes || []).join(','));
        if (response?.sections) {
            this.renderDiscountCodeLayout(
                response.sections,
                this._getAcceptedDiscountCodesFromResponse(response)
            );
        }
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.updateAndShowCart,
            document,
            {
                source: 'gift-promotion-discount-refresh',
            }
        );
        return response;
    }

    /**
     * Removes the coupon gift line from the latest cart state.
     *
     * @returns {Promise<boolean>} True when a line was removed.
     * @private
     */
    async _removeGiftPromotionLineFromCart() {
        const cart = await this._getCart();
        const giftLine = this._findGiftPromotionLine(cart);

        if (!giftLine?.key) return false;

        await this._changeCartLineQuantity(giftLine.key, 0);
        return true;
    }

    /**
     * Rolls back any coupon gift line and removes only the configured gift code.
     *
     * @param {string[]} fallbackCodes
     * @returns {Promise<void>}
     * @private
     */
    async _rollbackGiftPromotion(fallbackCodes = []) {
        let codesToPreserve = fallbackCodes;

        try {
            const cart = await this._getCart();
            const latestCodes = this._getDiscountCodesFromCart(cart);
            if (latestCodes.length) {
                codesToPreserve = latestCodes;
            }
            const giftLine = this._findGiftPromotionLine(cart);
            if (giftLine?.key) {
                await this._changeCartLineQuantity(giftLine.key, 0);
            }
        } catch (error) {
            console.error('Error removing promotional gift line:', error);
        }

        try {
            await this._applyDiscountCodesAndRefresh(
                this._withoutGiftCode(codesToPreserve)
            );
        } catch (error) {
            console.error('Error removing gift discount code:', error);
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.updateAndShowCart,
                document,
                {
                    source: 'gift-promotion-rollback-refresh',
                }
            );
        }
    }

    /**
     * Handles applying the configured gift coupon.
     *
     * @param {string} enteredCode
     * @param {string[]} existingCodes
     * @returns {Promise<void>}
     */
    async submitGiftPromotionDiscount(enteredCode, existingCodes) {
        if (
            this._giftPromotionInFlight ||
            window.__giftPromotionInFlight ||
            !this._isGiftPromotionConfigured()
        ) {
            return;
        }

        this._giftPromotionInFlight = true;
        window.__giftPromotionInFlight = true;
        const submitButton = this.querySelector('button[type="submit"]');
        submitButton?.classList.add('disable');
        this.hideAllDiscountMessages();

        let attemptedGiftAdd = false;
        let shouldRollbackGiftLine = false;

        try {
            const preliminaryCart = await this._getCart();
            const preliminarySubtotal =
                this._getGiftEligibleSubtotal(preliminaryCart);

            if (
                this.giftMinSubtotalCents > 0 &&
                preliminarySubtotal < this.giftMinSubtotalCents
            ) {
                this.showGiftPromotionMessage('belowMinimum');
                return;
            }

            const existingGiftLine =
                this._findGiftPromotionLine(preliminaryCart);
            const giftCodeAlreadyApplied = existingCodes.some((code) =>
                this._isGiftCode(code)
            );

            if (giftCodeAlreadyApplied && existingGiftLine) {
                this.emptyDiscountInput();
                return;
            }

            if (!existingGiftLine) {
                attemptedGiftAdd = true;
                await this._addGiftPromotionLine();
                shouldRollbackGiftLine = true;
            } else if (Number(existingGiftLine.quantity || 0) > 1) {
                await this._changeCartLineQuantity(existingGiftLine.key, 1);
                shouldRollbackGiftLine = true;
            } else {
                shouldRollbackGiftLine = true;
            }

            const discountPayload = this._getDiscountPayloadWithGift(
                existingCodes,
                enteredCode
            );
            const discountResponse = await this._applyDiscountRequest(
                discountPayload
            );

            if (!this._isGiftCodeApplicable(discountResponse)) {
                const acceptedCodes =
                    this._getAcceptedDiscountCodesFromResponse(
                        discountResponse
                    );
                const acceptedNonGiftCodes = this._withoutGiftCode(
                    acceptedCodes
                );
                await this._rollbackGiftPromotion(
                    acceptedNonGiftCodes.length ? acceptedNonGiftCodes : existingCodes
                );
                this.showGiftPromotionMessage(
                    acceptedNonGiftCodes.length
                        ? 'combinationConflict'
                        : 'alreadyUsed'
                );
                return;
            }

            this.checkDiscountCodeValidationResponse(discountResponse, 'add');
        } catch (error) {
            console.error('Error applying gift promotion:', error);
            if (attemptedGiftAdd || shouldRollbackGiftLine) {
                await this._rollbackGiftPromotion(existingCodes);
            }
            this.showGiftPromotionMessage(
                error.status === 422 ? 'unavailable' : 'networkError'
            );
        } finally {
            submitButton?.classList.remove('disable');
            this._giftPromotionInFlight = false;
            window.__giftPromotionInFlight = false;
        }
    }

    /**
     * Removes the configured gift coupon and coupon gift line.
     *
     * @param {string[]} preservedCodes
     * @returns {Promise<void>}
     */
    async removeGiftPromotionDiscount(preservedCodes) {
        if (!this._isGiftPromotionConfigured()) return;

        const submitButton = this.querySelector('button[type="submit"]');
        submitButton?.classList.add('disable');

        try {
            await this._removeGiftPromotionLineFromCart();
            await this._applyDiscountCodesAndRefresh(
                this._withoutGiftCode(preservedCodes)
            );
        } catch (error) {
            console.error('Error removing gift promotion:', error);
            this.showGiftPromotionMessage('genericError');
        } finally {
            submitButton?.classList.remove('disable');
        }
    }

    /**
     * Debounces coupon gift reconciliation after cart mutations.
     */
    scheduleGiftPromotionReconcile() {
        if (!this._isGiftPromotionConfigured()) return;

        clearTimeout(this._giftPromotionReconcileTimer);
        this._giftPromotionReconcileTimer = setTimeout(() => {
            this.reconcileGiftPromotion();
        }, 250);
    }

    /**
     * Keeps the coupon gift line and configured code aligned with cart state.
     *
     * @returns {Promise<void>}
     */
    async reconcileGiftPromotion() {
        if (
            this._giftPromotionInFlight ||
            window.__giftPromotionInFlight ||
            window.__giftPromotionReconcileInFlight ||
            !this._isGiftPromotionConfigured()
        ) {
            return;
        }

        window.__giftPromotionReconcileInFlight = true;

        try {
            const cart = await this._getCart();
            const giftLine = this._findGiftPromotionLine(cart);
            const codes = this._getDiscountCodesFromCart(cart);
            const hasGiftCode = codes.some((code) => this._isGiftCode(code));

            if (!giftLine && hasGiftCode) {
                await this._applyDiscountCodesAndRefresh(
                    this._withoutGiftCode(codes)
                );
                return;
            }

            if (!giftLine) return;

            const eligibleSubtotal = this._getGiftEligibleSubtotal(cart);

            if (
                this.giftMinSubtotalCents > 0 &&
                eligibleSubtotal < this.giftMinSubtotalCents
            ) {
                await this._changeCartLineQuantity(giftLine.key, 0);
                await this._applyDiscountCodesAndRefresh(
                    this._withoutGiftCode(codes)
                );
                return;
            }

            if (!hasGiftCode) {
                await this._changeCartLineQuantity(giftLine.key, 0);
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.updateAndShowCart,
                    document,
                    {
                        source: 'gift-promotion-line-without-code',
                    }
                );
                return;
            }

            if (Number(giftLine.quantity || 0) > 1) {
                await this._changeCartLineQuantity(giftLine.key, 1);
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.updateAndShowCart,
                    document,
                    {
                        source: 'gift-promotion-quantity-reconciled',
                    }
                );
            }
        } catch (error) {
            console.error('Error reconciling gift promotion:', error);
        } finally {
            window.__giftPromotionReconcileInFlight = false;
        }
    }

    /**
     * Checks response for any invalid newly added discount codes.
     *
     * @param {Array<Object>} discount_codes - List of discount code validation objects.
     * @param {string[]} existingDiscountCodes - Currently applied discount codes.
     * @return {boolean} True if there is an invalid new code.
     * @private
     */
    _hasInvalidDiscount(discount_codes, existingDiscountCodes) {
        return (discount_codes || []).some((discount) => {
            // Check for any key that is false
            const hasFalseKey = Object.values(discount).includes(false);
            // Check if code is not in the existingDiscountCodes
            const isNewCode = !existingDiscountCodes.includes(discount.code);
            return hasFalseKey && isNewCode;
        });
    }

    /**
     * Validates AJAX response, updates layout, and displays appropriate messages.
     *
     * @param {Object} response - AJAX response containing discount codes and sections.
     * @param {string} [discountAction] - Action type ('add') to trigger success messaging.
     * @private
     */
    checkDiscountCodeValidationResponse(response, discountAction) {
        const { discount_codes = [] } = response;
        const hideMessageTimeout = 2000;
        // Empty Discount input on validation
        this.emptyDiscountInput();
        // Invalid discount code and do not execute success methods
        if (
            this._hasInvalidDiscount(
                discount_codes,
                this._getExisitingDiscountsCode()
            )
        ) {
            // 1) If we find invalid discount code then show error
            this.showDiscountMessages('error');
            this.hideAllDiscountMessages(hideMessageTimeout);
            return;
        }

        // If Discount code is applicable run functionalities as follows:
        // 1) Re-render discount code layout
        const { sections } = response;
        // Check if the code is free shipping message code

        if (discountAction && discountAction === 'add') {
            const newDiscountsFromReponse = this._extractDiscountPillsFromHtml(
                sections['manual-discount-codes']
            );
            const exisitingDiscounts = this._getExisitingDiscountsCode();
            const noNewDiscount =
                Array.isArray(newDiscountsFromReponse) &&
                newDiscountsFromReponse.length === exisitingDiscounts.length &&
                newDiscountsFromReponse.every((code) =>
                    exisitingDiscounts.includes(code)
                );

            if (noNewDiscount) {
                this.showDiscountMessages('shipping-error');
                this.hideAllDiscountMessages(hideMessageTimeout);
                return;
            }
        }
        this.renderDiscountCodeLayout(
            sections,
            this._getAcceptedDiscountCodesFromResponse(response)
        );
        HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.updateAndShowCart);

        // 2) Show success message if discountAction type 'add'
        if (discountAction && discountAction === 'add') {
            this.showDiscountMessages('success');
        }
        this.hideAllDiscountMessages(hideMessageTimeout);
    }

    /**
     * Cleans up global listeners when section rendering replaces the component.
     */
    disconnectedCallback() {
        clearTimeout(this._giftPromotionReconcileTimer);
        document.removeEventListener(
            _EVENT_HELPER.triggerCartChange,
            this._boundScheduleGiftPromotionReconcile
        );
        document.removeEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this._boundScheduleGiftPromotionReconcile
        );
    }
}

customElements.get('discount-component') ||
    customElements.define('discount-component', DiscountComponent);

/**
 * RemoveDiscountPill represents a clickable element to remove a specific discount code badge.
 *
 * @class RemoveDiscountPill
 * @extends HTMLElement
 */
class RemoveDiscountPill extends HTMLElement {
    /**
     * Initializes the RemoveDiscountPill by setting up click listener.
     */
    constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.discountForm = this.querySelector(`#discount-${this.sectionId}`);
        this.addEventListener('click', this.removeDiscount.bind(this));
    }

    /**
     * Dispatches removeDiscountCode event and removes the badge element from the DOM.
     * @private
     */
    removeDiscount() {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.removeDiscountCode,
            this,
            {
                preventBubble: true,
                code: this.closest('[data-code]').dataset.code,
            }
        );
        this.closest('[data-code]').remove();
    }

    /**
     * Cleans up event listeners when element is removed from the DOM.
     * @private
     */
    disconnectedCallback() {
        this.removeEventListener('click', this.removeDiscount.bind(this));
    }
}

customElements.get('remove-discount-pill') ||
    customElements.define('remove-discount-pill', RemoveDiscountPill);
